import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { Card, CardActions, CardContent, IconButton } from '@mui/material';
import LoadingIconButton from 'core/components/LoadingIconButton';
import { extname } from 'core/utils/pathUtils';
import { printImage, printPdf } from 'core/utils/printUtils';
import saveAs from 'file-saver';
import { useMemo, useState } from 'react';
import * as fileService from 'services/orderFileService';
import OrderFileIcon from './OrderFileIcon';
import OrderFileTitle from './OrderFileTitle';
import type { OrderFile } from 'models/order/orderFile';
import type { FC } from 'react';

const OrderFileCard: FC<FileCardProps> = ({ file, onDelete }) => {
  const [printing, setPrinting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadClick = (): void => {
    setDownloading(true);
    fileService
      .getDownloadFileAsync(file)
      .then((blob) => {
        setDownloading(false);
        saveAs(blob, file.name);

        return downloading;
      })
      .catch((err) => {
        setDownloading(false);
        console.log(err);
      });
  };

  const onPrintClickHandle: VoidFunction = () => {
    setPrinting(true);
    fileService
      .getDownloadFileAsync(file)
      .then((file) => {
        setPrinting(false);

        fileExt === '.pdf' ? printPdf(file) : printImage(file);

        return printing;
      })
      .catch((err) => {
        setPrinting(false);
        console.log(err);
      });
  };

  const fileExt = useMemo(() => extname(file.name).toLowerCase(), [file]);

  const isPrintableFile = useMemo(
    () => ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'].includes(fileExt),
    [fileExt]
  );

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
      variant='outlined'>
      <CardContent
        sx={{ flexGrow: 1, rowGap: 1, display: 'flex', flexDirection: 'column' }}>
        <OrderFileIcon file={file} />
        <OrderFileTitle title={file.name} />
      </CardContent>
      <CardActions disableSpacing>
        <LoadingIconButton
          isLoading={downloading}
          onClick={handleDownloadClick}
          aria-label='Скачать'>
          <FileDownloadOutlinedIcon />
        </LoadingIconButton>

        {isPrintableFile && (
          <LoadingIconButton
            isLoading={printing}
            onClick={onPrintClickHandle}
            aria-label='Печать'>
            <PrintOutlinedIcon />
          </LoadingIconButton>
        )}

        <IconButton
          onClick={onDelete}
          aria-label='Удалить'
          color='warning'
          sx={{ ml: 'auto' }}>
          <DeleteOutlinedIcon />
        </IconButton>
      </CardActions>
    </Card>
  );
};

export interface FileCardProps {
  file: OrderFile;
  orderId: number;

  onDelete?: VoidFunction;
}

export default OrderFileCard;
